import { create } from 'zustand';
import api from '@/features/api/axios';
import { logger } from '@/features/help/utils/logger';
import { toast } from 'react-hot-toast';

interface Price {
  id: string;
  nickname?: string;
  unit_amount: number;
  currency: string;
  type?: 'recurring' | 'metered';
  recurring?: {
    interval: string;
  };
}

interface SubscriptionState {
  isSubscribed: boolean;
  isLoading: boolean;
  error: string | null;
  prices: Price[] | null;
  standardUsageCount: number;
  premiumUsageCount: number;
  freeUsageCount: number;
  totalUsageCount: number;
  standardApplicationPrice: Price | null; // Price for standard application processing
  premiumApplicationPrice: Price | null; // Price for premium application processing
  currentPeriodEnd: Date | null;
  isModalOpen: boolean;
  status: string | null;
  freeTrialLimit: number;
  freeTrialRemaining: number;
  checkSubscription: () => Promise<void>;
  createSubscription: () => Promise<string>; // Returns Stripe Checkout URL
  cancelSubscription: () => Promise<boolean>; // Returns true on success, false on failure
  openSubscriptionModal: () => void;
  closeSubscriptionModal: () => void;
}

// Define free trial limit constant
export const FREE_TRIAL_LIMIT = 20;

export const useSubscriptionStore = create<SubscriptionState>((set, get) => ({
  isSubscribed: false,
  isLoading: false,
  error: null,
  prices: null,
  standardUsageCount: 0,
  premiumUsageCount: 0,
  freeUsageCount: 0,
  totalUsageCount: 0,
  standardApplicationPrice: null,
  premiumApplicationPrice: null,
  currentPeriodEnd: null,
  isModalOpen: false,
  status: null,
  freeTrialLimit: FREE_TRIAL_LIMIT,
  freeTrialRemaining: FREE_TRIAL_LIMIT,

  checkSubscription: async () => {
    try {
      set({ isLoading: true, error: null });
      const response = await api.get('/api/subscription/status');

      const standardUsageCount = response.data.standardUsageCount || 0;
      const premiumUsageCount = response.data.premiumUsageCount || 0;
      const freeUsageCount = response.data.freeUsageCount || 0;
      const totalUsageCount = response.data.totalUsageCount || 0;
      const freeTrialRemaining = response.data.freeTrialRemaining || 0;
      const isSubscribed = response.data.isSubscribed || false;

      logger.info('Subscription', 'Subscription status checked', {
        isSubscribed,
        standardUsageCount,
        premiumUsageCount,
        freeUsageCount,
        totalUsageCount,
        freeTrialRemaining,
        status: response.data.status,
        endpoint: '/api/subscription/status',
      });

      set({
        isLoading: false,
        isSubscribed,
        standardUsageCount,
        premiumUsageCount,
        freeUsageCount,
        totalUsageCount,
        currentPeriodEnd: response.data.currentPeriodEnd
          ? new Date(response.data.currentPeriodEnd)
          : null,
        status: response.data.status,
        standardApplicationPrice: response.data.standardApplicationPrice,
        premiumApplicationPrice: response.data.premiumApplicationPrice,
        freeTrialRemaining,
      });
    } catch (error) {
      logger.error('Subscription', 'Failed to check subscription', {
        error,
      });
      set({
        isLoading: false,
        error:
          error instanceof Error ? error.message : 'An error occurred while checking subscription',
      });
    }
  },

  createSubscription: async () => {
    try {
      const response = await api.post('/api/subscription/create-checkout');
      return response.data.url;
    } catch (error) {
      logger.error('Subscription', 'Failed to create checkout session', {
        error,
      });
      set({
        error:
          error instanceof Error ? error.message : 'An error occurred while creating subscription',
      });
      throw error;
    }
  },

  cancelSubscription: async () => {
    logger.info('SubscriptionStore', 'Initiating subscription cancellation');
    set({ isLoading: true, error: null }); // Indicate loading state
    try {
      // Call the backend endpoint to cancel the subscription
      await api.post('/api/subscription/cancel');

      logger.info(
        'SubscriptionStore',
        'Subscription cancellation request successful. Refreshing status.'
      );
      // Refresh the subscription status to get the 'active_until_period_end' state
      await get().checkSubscription(); // Use get() to call another action

      set({ isLoading: false }); // Clear loading state
      toast.success('Subscription successfully scheduled for cancellation.');
      return true; // Indicate success
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      logger.error('SubscriptionStore', 'Failed to cancel subscription', {
        error: errorMessage,
        // Attempt to get response data if available (e.g., from axios error)
        responseData: (error as any)?.response?.data,
      });
      set({
        isLoading: false,
        error: `Failed to cancel subscription: ${errorMessage}`,
      });
      toast.error(`Failed to cancel subscription: ${errorMessage}`);
      return false; // Indicate failure
    }
  },

  openSubscriptionModal: () => {
    set({ isModalOpen: true });
  },

  closeSubscriptionModal: () => {
    set({ isModalOpen: false });
  },
}));
